<template>
    <div class="Projects">
        <q-card
            class="ProjectsCard"
            bordered
        >
            <q-card-section class="ProjectsBody">
                <ProjectsList
                    ref="table"
                    :rows="projects"
                    :total="total"
                    :search="headers.search"
                    :loading="isFetching"
                    :pagination="pagination"
                    @on-new-item="handleNewItem"
                    @on-delete="handleDeleteItem"
                    @on-update="handleUpdateItem"
                    @on-click="handleClickItem"
                    @on-fetch-data="updatePagination"
                />
            </q-card-section>
        </q-card>

        <ModalRequest
            ref="delPrDialog"
            :title="$t('projects.delete')"
            :text="$t('projects.modals.delete.text', { name: (selected || {}).name })"
            :loading="isLoading"
            @on-accept="archiveProject"
        />
        <ModalNameDesc
            ref="editPrDialog"
            :title="$t('projects.edit')"
            :action="$t('actions.save')"
            :loading="isLoading"
            @on-submit="updateProject"
        />
        <ModalNameDesc
            ref="newPrDialog"
            :title="$t('projects.new')"
            :action="$t('actions.create')"
            :loading="isLoading"
            @on-submit="createProject"
        />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, notifyError, notifySuccess,
} from '@/api'
import { dialogMixin } from '@/mixins'
import { ModalRequest, ModalNameDesc } from '@/components'
import { ProjectsList } from './sections'

export default {
    name: 'ProjectsView',
    components: {
        ModalRequest,
        ModalNameDesc,
        ProjectsList,
    },
    mixins: [dialogMixin],
    props: {
        headers: VueTypes.shape({
            search: VueTypes.string.def(''),
            archived: VueTypes.bool.def(false),
        }),
    },
    data() {
        return {
            projects: undefined,
            total: undefined,
            isFetching: false,
            isLoading: false,
            pagination: {},
            header: {
                search: '',
                archived: false,
            },
        }
    },
    computed: {
        params() {
            return {
                ...this.headers,
                ...this.pagination,
            }
        },
    },
    watch: {
        headers(filters) {
            if (filters) this.fetchData()
        },
    },
    beforeMount() {
        const { organizationId } = this.$route.params || {}
        this.organizationId = organizationId
    },
    mounted() {
        const { q } = this.$route.query || {}
        this.search = q || ''

        // Paginación robusta
        let paginationMap = {}
        try {
            const storedPaginations = localStorage.getItem('projectsPagination')
            paginationMap = storedPaginations ? JSON.parse(storedPaginations) : {}
        } catch (e) {
            paginationMap = {}
        }
        if (paginationMap[this.organizationId]) {
            this.pagination = paginationMap[this.organizationId]
        } else {
            this.pagination = { page: 1, rowsPerPage: 25 }
            paginationMap[this.organizationId] = this.pagination
            try {
                localStorage.setItem('projectsPagination', JSON.stringify(paginationMap))
            } catch (e) { /* ignore write errors */ }
        }

        // SortDirection robusto
        let sortDirection = 'ASCENDING'
        try {
            const storedSortDirection = localStorage.getItem('projectsListSortDirection')
            if (storedSortDirection && ['ASCENDING', 'DESCENDING'].includes(storedSortDirection)) {
                sortDirection = storedSortDirection
            } else {
                localStorage.setItem('projectsListSortDirection', sortDirection)
            }
        } catch (e) {
            localStorage.setItem('projectsListSortDirection', sortDirection)
        }

        // SortBy robusto
        let sortBy = 'NAME'
        try {
            const storedSortBy = localStorage.getItem('projectsListSortBy')
            if (storedSortBy && ['NAME', 'DATE', 'OWNER'].includes(storedSortBy)) {
                sortBy = storedSortBy
            } else {
                localStorage.setItem('projectsListSortBy', sortBy)
            }
        } catch (e) {
            localStorage.setItem('projectsListSortBy', sortBy)
        }

        this.params.sortBy = sortBy
        this.params.sortDirection = sortDirection
        this.fetchData()
    },
    methods: {
        handleSearch({ search }) {
            if (!search) this.$router.replace(this.$router).catch(() => {})
            this.fetchData()
        },
        handleClickItem(projectId) {
            this.$router.push({ name: 'Project', params: { organizationId: this.organizationId, projectId } })
        },
        updatePagination(props) {
            this.pagination = { ...this.pagination, ...props }
            const storedPaginations = localStorage.getItem('projectsPagination')
            const paginationMap = storedPaginations ? JSON.parse(storedPaginations) : {}

            paginationMap[this.organizationId] = this.pagination
            localStorage.setItem('projectsPagination', JSON.stringify(paginationMap))
            this.fetchData()
        },
        async fetchData() {
            this.isFetching = true
            const {
                archived, search,
            } = this.params
            const { page, rowsPerPage } = this.pagination
            const sortDirection = localStorage.getItem('projectsListSortDirection')
            const sortBy = localStorage.getItem('projectsListSortBy')
            const params = {
                organizationUid: this.organizationId,
                includeArchived: !!archived,
                ...(rowsPerPage ? { start: (page - 1) * rowsPerPage } : {}),
                ...(rowsPerPage ? { limit: rowsPerPage } : {}),
                sortBy,
                sortDirection,
                search: search || null,
            }
            apiRequest(Api().projects.list(params))
                .then(({ data, total }) => {
                    this.projects = data
                    this.total = total
                })
                .catch(notifyError)
                .finally(() => { this.isFetching = false })
        },
        async createProject({ name, description }) {
            this.isLoading = true
            apiRequest(Api().projects.create({ organizationUid: this.organizationId, name, description }))
                .then(() => {
                    notifySuccess(this.$t('projects.notifications.created.text'))
                    this.fetchData()
                    this.$refs.newPrDialog.close()
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        async updateProject({ uuid, name, description }) {
            this.isLoading = true
            apiRequest(Api().projects.update({ projectId: uuid, params: { name, description } }))
                .then(() => {
                    notifySuccess(this.$t('projects.notifications.updated.text'))
                    this.fetchData()
                    this.$refs.editPrDialog.close()
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        async archiveProject() {
            const { uuid } = this.selected
            this.isLoading = true
            apiRequest(Api().projects.delete({ projectId: uuid }))
                .then(() => {
                    notifySuccess(this.$t('projects.notifications.deleted.text'))
                    this.fetchData()
                    this.$refs.delPrDialog.close()
                    this.selected = undefined
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
    },
}
</script>

<style scoped lang="scss">
.ProjectsCard {
    background: $white;
}

.ProjectsBody {
    padding: 16px;
}
</style>
